import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import PublicShell from "./components/PublicShell";
import SecurityCheck from "./components/SecurityCheck";
import { DoorControl, GateControl } from "./components/AccessControls";
import { ClockIcon, LockIcon } from "./components/accessIcons";
import { accessApi, apiErrorMessage, readAccessSession, writeAccessSession } from "./publicVehicleApi";
import styles from "./publicvehicle.module.css";

const TITLE = "Parking Access";
const TARGET_NAMES = { gate: "Gate", door: "Door" };

const formatDate = (value) => (value ? dayjs(value).format("ddd, MMM D · h:mm A") : "");
const formatDay = (value) => (value ? dayjs(value).format("MMM D, YYYY") : "");

// Board 24: pass ended, cancelled or revoked
const InactiveScreen = ({ data }) => {
  const expired = data?.state === "expired";
  const label = data?.pass?.label || "parking pass";
  const plate = data?.vehicle?.license || "your vehicle";
  const host = data?.host || {};
  const hostText = host.name ? `${host.name}${host.apt_no ? ` (Apt Unit ${host.apt_no})` : ""}` : `your host${host.apt_no ? ` (Apt Unit ${host.apt_no})` : ""}`;

  return (<>
    <section className={`${styles.card} ${styles.centerText}`}>
      <div className={styles.gaGreyCircle}><LockIcon size={26} /></div>
      <h2 className={styles.cardTitle}>{expired ? "This access link has expired" : "This access link is no longer active"}</h2>
      <p className={styles.cardText}>
        {expired && data?.pass?.expires_at
          ? `Your ${label} for ${plate} ended on ${formatDay(data.pass.expires_at)}, so the gate and door controls are no longer available.`
          : `Your ${label} for ${plate} is no longer active, so the gate and door controls are no longer available.`}
      </p>
      <div className={styles.gaInfoBox}>
        Visiting again? Ask {hostText} to add you as a guest
        {data?.property_phone ? <>, or call <a href={"tel:" + data.property_phone}>{data.property_phone}</a></> : ""}.
      </div>
    </section>
  </>);
};

const GuestAccess = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [inactive, setInactive] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [session, setSession] = useState(() => readAccessSession(token));
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [target, setTarget] = useState("");

  const clearSession = useCallback(() => {
    writeAccessSession(token, "", null);
    setSession("");
  }, [token]);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const response = await accessApi.get(token);
      const next = response?.data?.data || null;
      setData(next);
      setInactive(false);
      // A session saved on this phone may have been revoked or ended early
      const saved = readAccessSession(token);
      if (next && saved) {
        try {
          const check = await accessApi.session(token, saved);
          if (!check?.data?.data?.valid) clearSession();
        } catch (err) {
          console.error("[GuestAccess] Failed to check session", err);
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410 && err?.response?.data?.data) {
        clearSession();
        setData(err.response.data.data);
        setInactive(true);
      } else if (status === 404) {
        setNotFound(true);
      } else {
        console.error("[GuestAccess] Failed to load access link", err);
        setLoadError(apiErrorMessage(err, "Unable to load this link. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  }, [token, clearSession]);

  useEffect(() => {
    load();
  }, [load]);

  const controls = data?.controls || {};
  const available = ["gate", "door"].filter((key) => controls[key]);
  const current = available.includes(target) ? target : available[0] || "";

  const sendCode = async () => {
    try {
      const response = await accessApi.sendCode(token);
      return response?.data?.phone_hint || "";
    } catch (err) {
      console.error("[GuestAccess] Failed to send code", err);
      if (err?.response?.status === 410 || err?.response?.status === 404) load();
      throw apiErrorMessage(err, "Unable to send the code. Please try again.");
    }
  };

  const verifyCode = async (code) => {
    try {
      const response = await accessApi.verifyCode(token, code);
      const value = response?.data?.session_token || "";
      writeAccessSession(token, value, response?.data?.session_expires_at);
      setNotice("");
      setError("");
      setSession(value);
    } catch (err) {
      console.error("[GuestAccess] Failed to verify code", err);
      if (err?.response?.status === 410 || err?.response?.status === 404) load();
      const left = err?.response?.data?.attempts_left;
      const message = apiErrorMessage(err, "Unable to verify the code. Please try again.");
      throw left !== undefined && left !== null ? `${message} ${left} ${left === 1 ? "attempt" : "attempts"} left.` : message;
    }
  };

  // Gate or door; resolves true when the device accepted the command
  const open = async (which) => {
    setError("");
    try {
      await accessApi.open(token, which, session);
      return true;
    } catch (err) {
      console.error("[GuestAccess] Failed to open " + which, err);
      const status = err?.response?.status;
      if (status === 401 || err?.response?.data?.reverify) {
        clearSession();
        setNotice("Your session ended. Please enter a new code to keep using the gate and door.");
      } else if (status === 410) {
        // Pass ended or was cancelled meanwhile: reload shows the expired screen
        load();
      } else if (status === 429) {
        setError("Please wait a moment and try again.");
      } else {
        setError(apiErrorMessage(err, `Unable to open the ${which} right now. Please try again.`));
      }
      return false;
    }
  };

  const signOut = async () => {
    const value = session;
    clearSession();
    try {
      await accessApi.logout(token, value);
    } catch (err) {
      console.error("[GuestAccess] Failed to sign out", err);
    }
  };

  const shellProps = {
    propertyName: data?.property_name,
    propertyAddress: data?.property_address,
    propertyPhone: data?.property_phone,
  };

  if (loading) {
    return (<PublicShell title={TITLE}><div className={styles.center}><CircularProgress size={30} /></div></PublicShell>);
  }

  if (notFound) {
    return (<PublicShell title={TITLE}><Alert severity="error">This link is not valid.</Alert></PublicShell>);
  }

  if (!data) {
    return (<PublicShell title={TITLE}>
      <Alert severity="error">{loadError || "Unable to load this link. Please try again."}</Alert>
      <div className={styles.actions}><button className={styles.primaryButton} onClick={() => { setLoading(true); load(); }}>TRY AGAIN</button></div>
    </PublicShell>);
  }

  const context = [data.guest_first_name ? `Hi ${data.guest_first_name}` : "", data.property_address].filter(Boolean).join(" · ");

  if (inactive || data.state !== "active") {
    return (<PublicShell title={TITLE} context={context} {...shellProps}>
      <InactiveScreen data={data} />
    </PublicShell>);
  }

  if (!session) {
    return (<PublicShell title={TITLE} context={context} {...shellProps}>
      <SecurityCheck phoneHint={data.phone_hint} notice={notice} sendCode={sendCode} verifyCode={verifyCode} />
    </PublicShell>);
  }

  const pass = data.pass || {};
  const plate = data.vehicle?.license;

  return (<PublicShell title={TITLE} context={context} {...shellProps}>
    <section className={styles.gaPassCard}>
      <div>
        <p className={styles.gaPassTitle}>{[pass.label || "Guest parking pass", plate].filter(Boolean).join(" · ")}</p>
        {pass.expires_at && <p className={styles.muted}>Valid until {formatDate(pass.expires_at)}</p>}
      </div>
      <span className={`${styles.chip} ${styles.chip_approved}`}>Active</span>
    </section>

    <section className={styles.gaPanel}>
      <p className={styles.gaPanelLabel}>{available.length > 1 ? "PARKING ACCESS — TAP A BUTTON TO SWITCH" : "PARKING ACCESS"}</p>
      {available.length > 1 && (<div role="tablist" aria-label="Control" className={styles.gaTabs}>
        {available.map((key) => (<button
          key={key}
          type="button"
          role="tab"
          aria-selected={current === key}
          className={current === key ? `${styles.gaTab} ${styles.gaTabOn}` : styles.gaTab}
          onClick={() => { setTarget(key); setError(""); }}
        >
          {TARGET_NAMES[key]}
        </button>))}
      </div>)}

      {available.length === 0 && <p className={styles.gaEmpty}>No gate controls are set up at this property.</p>}
      {current !== "" && (<>
        <p className={styles.gaNow}>Now controlling the <strong>{TARGET_NAMES[current]}</strong></p>
        {error !== "" && <Alert severity="error">{error}</Alert>}
        {current === "gate" ? <GateControl key="gate" onOpen={() => open("gate")} /> : <DoorControl key="door" onOpen={() => open("door")} />}
      </>)}
    </section>

    <section className={styles.gaNote}>
      <span className={styles.gaNoteIcon}><ClockIcon /></span>
      <p>
        This link stops working when your pass ends{pass.expires_at ? ` on ${formatDate(pass.expires_at)}` : ""} or if it is cancelled.
        Lost it? Reply LINK to the text message.
      </p>
    </section>

    <p className={styles.lockNote}>Not your phone? <button type="button" className={styles.linkButton} onClick={signOut}>Sign out</button></p>
  </PublicShell>);
};

export default GuestAccess;
